import styles from '../drawermenu.module.css';

import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import {
    DrawerContent as KendoDrawerContent,
} from '@progress/kendo-react-layout';
import { toggleTheme } from "components/helpers/ThemeHelpers";
import { sleep } from "components/helpers/DataHelpers";
import { isNullOrEmpty } from "components/helpers/ObjectHelpers";
import { DrawerMenuItem } from "./DrawerMenuItem";
import { getElementById } from "components/helpers/ElementHelpers";
import { IconPicker } from "components/layout/icons/IconPicker";
import { useImpersonateClassName } from "../../../../hooks/useImpersonateClassName";
import { NavItemTypes } from "services/SystemNames";
import { useDarkmode, useSetDarkmode } from '../../../../hooks/useAppStore';

interface DrawerProps {
    mode: string,
    expanded: boolean,
    mini: boolean,
    isNewVersionAvailable: boolean,
    items: any[],
    setExpanded: (expanded: boolean) => void,
    onItemClick: (item: any) => void,
}
export const Drawer = ({
    children,
    mode,
    expanded,
    isNewVersionAvailable,
    mini,
    items,
    setExpanded,
    onItemClick,
}: PropsWithChildren<DrawerProps>) => {

    const modeClassName = mode === "push" ? "k-drawer-push" : "k-drawer-overlay";
    const miniClassName = expanded ? "" : mini ? "k-drawer-mini" : "";
    const expandedClassName = expanded ? "k-drawer-expanded" : "";

    const [drawerStyle, setDrawerStyle] = useState<any>(null);
    const [currentMode, setCurrentMode] = useState<string>(mode);

    const [hasScrolledToTop, setHasScrolledToTop] = useState<boolean>(true);
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState<boolean>(true);

    const wrapperRef = useRef<HTMLDivElement>(null);

    const darkmode = useDarkmode();
    const setDarkmode = useSetDarkmode();

    const impersonateClassName = useImpersonateClassName();

    useEffect(() => {
        const checkScrollable = () => {
            if (wrapperRef.current) {
                const tracker = wrapperRef.current;
                const isScrollable = tracker.scrollHeight > tracker.clientHeight;

                if (isScrollable) {
                    onScroll();
                } else {
                    setHasScrolledToTop(true);
                    setHasScrolledToBottom(true);
                }
            } else {
                setHasScrolledToTop(true);
                setHasScrolledToBottom(true);
            }
        };

        const checkResizeScrollable = () => {
            checkScrollable();
        }
        const checkMutationScrollable = async () => {
            await sleep(500); // wait for slide to finish
            checkScrollable();
        }

        const resizeObserver = new ResizeObserver(checkResizeScrollable);
        const mutationObserver = new MutationObserver(checkMutationScrollable);

        if (wrapperRef.current) {
            resizeObserver.observe(wrapperRef.current);
            mutationObserver.observe(wrapperRef.current, { childList: true, subtree: true });
        }

        return () => {
            if (wrapperRef.current) {
                resizeObserver.unobserve(wrapperRef.current);
                mutationObserver.disconnect();
            }
        };
    }, []);

    const onDrawerStyleChange = (transition: number) => {
        const transitionValue = `${transition}ms`;
        if (expanded) {
            setDrawerStyle({ opacity: 1, transition: transitionValue, flexBasis: "240px" });
        } else {
            if (mode === "overlay") {
                setDrawerStyle({ opacity: 0, transform: "translateX(100%)", transition: transitionValue });
            } else {
                const flexBasis = mini ? "48px" : "0px";
                setDrawerStyle({ opacity: 1, transition: transitionValue, flexBasis: flexBasis });
            }
        }

        if (currentMode === mode) return;
        setCurrentMode(mode);
    }

    useEffect(() => {
        onDrawerStyleChange(400);
    }, [expanded]);

    useEffect(() => {
        onDrawerStyleChange(0);
    }, [mini, mode]);

    useEffect(() => {
        const content: any | null = getElementById("DarkModeIcon");
        if (!content) return;

        content.classList.add(styles.iconExit);

        setTimeout(() => {
            if (!darkmode) {
                content.classList.remove(styles.sunIcon);
                content.classList.add(styles.moonIcon);
            } else {
                content.classList.remove(styles.moonIcon);
                content.classList.add(styles.sunIcon);
            }

            content.classList.remove(styles.iconExit);
            content.classList.add(styles.iconEnter);

        }, 400); // Match the duration of the animation
    }, [darkmode]);

    const getThemeIcon = () => {
        const theme = localStorage.getItem("theme");
        const isDarkMode = !isNullOrEmpty(theme) && theme === "theme-dark"
        return isDarkMode ? styles.moonIcon : styles.sunIcon;
    }

    return (
        <div className={`k-drawer-container ${expandedClassName} ${modeClassName} ${miniClassName}`}>
            {
                (mode === "overlay" && expanded) &&
                <div className="k-overlay" onClick={onClose} />
            }
            <div className={`k-drawer k-drawer-end ${impersonateClassName}`} style={drawerStyle}>
                <div className={`${styles.drawerMenuHeader}`}>
                    <div>
                        <div className={`${styles.darkModeButton} text-center`} onClick={onToggleDarkMode}>
                            <div id="DarkModeIcon" className={`${styles.icon} ${getThemeIcon()}`}></div>
                        </div>
                    </div>
                    <div/>
                    <div className={`${styles.icon} ${styles.closeIcon} pointer`} onClick={onClose}/>
                </div>
                <div ref={wrapperRef} className="k-drawer-wrapper" style={{ width: 240 }} onScroll={onScroll}>
                    {
                        (!hasScrolledToTop) &&
                        <div className={`${styles.drawerWrapperUp}`}><IconPicker icon="arrow-circle-up" size={32} opacity={0.2} /></div>
                    }
                    <ul className={`k-drawer-items ${styles.drawerMenuItems}`} role="menubar" aria-orientation="vertical">
                        {
                            items.map((item, index) =>
                                <DrawerMenuItem
                                    key={index}
                                    itemProps={item}
                                    onClick={onItemClick}
                                    isNewVersionAvailable={isNewVersionAvailable}
                                    isMenuExpanded={expanded}
                                    mini={mini}
                                />
                            )
                        }
                    </ul>
                    {
                        (!hasScrolledToBottom) &&
                        <div className={`${styles.drawerWrapperDown}`}><IconPicker icon="arrow-circle-down" size={32} opacity={0.2} /></div>
                    }

                </div>
            </div>
            <KendoDrawerContent>
                {children}
            </KendoDrawerContent>
        </div>
    )

    function onToggleDarkMode() {
        toggleTheme();
        setDarkmode(!darkmode);
    }

    function onClose() {
        setExpanded(false);
    }

    function onScroll() {
        if (!wrapperRef.current) return;

        const tracker = wrapperRef.current;
        const limit = tracker.scrollHeight - tracker.clientHeight;
        // Check if the div has scrolled to the top
        if (tracker.scrollTop === 0) {
            setHasScrolledToTop(true);
        } else {
            setHasScrolledToTop(false);
        }

        // Check if the div has scrolled to the bottom
        if ((tracker.scrollTop + 25) >= limit) {
            setHasScrolledToBottom(true);
        } else {
            setHasScrolledToBottom(false);
        }
    }
}
